import React, { useState } from 'react';
import RatingStars from './RatingStars';
import './ReviewItem.css';

/**
 * ReviewItem Component
 * Displays a single product review
 *
 * Props:
 * - review: object with rating, title, comment, reviewer_name, created_at, verified_purchase, helpful_count, not_helpful_count
 * - onVote: function(reviewId, isHelpful) - callback for voting
 */
const ReviewItem = ({ review, onVote }) => {
  const [voteStatus, setVoteStatus] = useState(null); // 'helpful', 'not_helpful', or null
  const [localHelpfulCount, setLocalHelpfulCount] = useState(review.helpful_count || 0);
  const [localNotHelpfulCount, setLocalNotHelpfulCount] = useState(review.not_helpful_count || 0);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleVote = async (isHelpful) => {
    if (voteStatus !== null) {
      // User already voted
      return;
    }

    try {
      await onVote(review.id, isHelpful);

      // Update local counts optimistically
      if (isHelpful) {
        setLocalHelpfulCount(prev => prev + 1);
        setVoteStatus('helpful');
      } else {
        setLocalNotHelpfulCount(prev => prev + 1);
        setVoteStatus('not_helpful');
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  return (
    <div className="review-item">
      <div className="review-header">
        <div className="review-rating">
          <RatingStars rating={review.rating} size="small" />
        </div>
        <h4 className="review-title">{review.title}</h4>
      </div>

      <div className="review-meta">
        <span className="reviewer-name">{review.reviewer_name}</span>
        {review.verified_purchase === 1 && (
          <span className="verified-badge" title="Verified Purchase">
            ✓ Verified Purchase
          </span>
        )}
        <span className="review-date">{formatDate(review.created_at)}</span>
      </div>

      <p className="review-comment">{review.comment}</p>

      <div className="review-actions">
        <span className="helpful-label">Was this review helpful?</span>
        <div className="vote-buttons">
          <button
            className={`vote-button ${voteStatus === 'helpful' ? 'vote-active' : ''}`}
            onClick={() => handleVote(true)}
            disabled={voteStatus !== null}
            aria-label="Mark as helpful"
          >
            Helpful ({localHelpfulCount})
          </button>
          <button
            className={`vote-button ${voteStatus === 'not_helpful' ? 'vote-active' : ''}`}
            onClick={() => handleVote(false)}
            disabled={voteStatus !== null}
            aria-label="Mark as not helpful"
          >
            Not Helpful ({localNotHelpfulCount})
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewItem;
